import type { AxiosInstance } from "axios";
import formatUrl from "utils/formatUrl";
import { z } from "zod";

import apiUrls from "config/urls/api";

import { FetchInstance } from "./interceptors";
import {
  Company,
  UpdateCompanyBody,
  UpdateCompanyResponse,
  companySchema,
  updateCompanyBodySchema,
  updateCompanyResponseSchema,
} from "./schema/companies/companySchema";
import { PromptBody } from "./schema/insights/prompts";
import {
  FileDownload,
  SpendInsightsCompanies,
  spendInsightsCompaniesSchema,
} from "./schema/insights/spendInsightsCompaniesSchema";
import {
  AllThreadsResponse,
  CreatedThread,
  FeedbackRequestBody,
  FeedbackResponse,
  InjectReportsRequestBody,
  ThreadMessages,
  allThreadsResponseSchema,
  createdThreadSchema,
  feedbackRequestBodySchema,
  feedbackResponseSchema,
  threadMessagesSchema,
} from "./schema/insights/threads";
import { ProductSettings, productSettingSchema } from "./schema/productsSchema";
import { ApiClient } from "./setup";
import { extractFileNameFromResponse } from "./shared";

export type FileOutputType =
  | "excel"
  | "spendSummary"
  | "payTechExcel"
  | "mastercardMatchExcel"
  | "suppliers"
  | "paymentMethods";
type FileExtension = "pptx" | "xlsx";

const getFileExtension = (outputType: FileOutputType): FileExtension => {
  switch (outputType) {
    case "excel":
    case "payTechExcel":
    case "mastercardMatchExcel":
      return "xlsx";
    case "spendSummary":
      return "pptx";
    default:
      return "xlsx";
  }
};

export class PublicApiClient extends ApiClient {
  constructor(axiosInstance: AxiosInstance, fetchInstance: FetchInstance) {
    super(axiosInstance, fetchInstance);
  }

  async updateCompany(
    { companyId }: { companyId: string },
    requestData: UpdateCompanyBody
  ): Promise<UpdateCompanyResponse> {
    return await this.patch({
      url: formatUrl(apiUrls.companies.one, { companyId }),
      body: requestData,
      requestSchema: updateCompanyBodySchema,
      responseSchema: updateCompanyResponseSchema,
    });
  }

  async addProductToCompany(params: {
    companyId: string;
    productKey: string;
  }): Promise<unknown> {
    return await this.put({
      url: formatUrl(apiUrls.companies.products.one, params),
      requestSchema: z.unknown(),
      responseSchema: z.unknown(),
    });
  }

  async removeProductFromCompany(params: {
    companyId: string;
    productKey: string;
  }): Promise<unknown> {
    return await this.delete({
      url: formatUrl(apiUrls.companies.products.one, params),
      responseSchema: z.unknown(),
    });
  }

  async getProductSettings<Response extends ProductSettings>(
    {
      productKey,
    }: {
      productKey: string;
    },
    { responseSchema = productSettingSchema }: { responseSchema: z.ZodSchema }
  ): Promise<Response> {
    return await this.get<Response>(
      formatUrl(apiUrls.products.settings.one, { productKey }),
      responseSchema
    );
  }

  async getSpendInsightsCompanies({
    query,
  }: {
    query: {
      page: number;
      pageSize: number;
      query?: string;
      orderBy?: string;
    };
  }): Promise<SpendInsightsCompanies> {
    return await this.get<SpendInsightsCompanies>(
      formatUrl(apiUrls.companies.spendInsights, {}, query),
      spendInsightsCompaniesSchema
    );
  }

  async getSpendReport({
    params,
  }: {
    params: {
      companyId: string;
      reportType: string;
      reportId?: string;
      outputName: FileOutputType;
    };
  }): Promise<FileDownload> {
    const { reportType, reportId } = params;
    // Use raw axios instance to handle blob response
    return this.axiosInstance
      .get(formatUrl(apiUrls.insights.company.downloadReport, params), {
        responseType: "blob",
      })
      .then(({ data, headers }) => {
        const contentDisposition = headers["content-disposition"];
        const fileName = extractFileNameFromResponse(contentDisposition);
        const fileExtension = getFileExtension(params.outputName);
        return {
          data,
          fileName: fileName ?? `${reportType}-${reportId}.${fileExtension}`,
        };
      });
  }

  async getCompany(params: { companyId: string }): Promise<Company> {
    return await this.get(
      formatUrl(apiUrls.companies.one, params),
      companySchema
    );
  }

  async getInsightsThreadMessages(params: {
    companyId: string;
    threadId: string;
  }): Promise<ThreadMessages> {
    return await this.get<ThreadMessages>(
      formatUrl(apiUrls.insights.company.thread.messages, params, {
        limit: 50,
      }),
      threadMessagesSchema
    );
  }

  async createInsightsForReports(
    params: {
      companyId: string;
      threadId: string;
    },
    body: InjectReportsRequestBody,
    callback: (chunk: string) => void
  ) {
    return this.streamResponse(
      formatUrl(apiUrls.insights.company.thread.injectReports, params),
      callback,
      {
        method: "POST",
        body: JSON.stringify(body),
      }
    );
  }

  async createInsightsThread(params: {
    companyId: string;
    reportType: "spendAnalysis";
    reportOutput: "suppliers";
    reportId: "latest";
  }) {
    return await this.post<unknown, CreatedThread>({
      url: formatUrl(apiUrls.insights.company.threads, params),
      requestSchema: z.unknown(),
      responseSchema: createdThreadSchema,
    });
  }

  async deleteInsightsThread(params: { companyId: string; threadId: string }) {
    return await this.delete<unknown>({
      url: formatUrl(apiUrls.insights.company.thread.one, params),
      responseSchema: z.unknown(), // We don't use the returned value so don't care about the type
    });
  }

  async getThreadsForCompany(
    params: { companyId: string },
    query: { pageNumber: number; pageSize?: number }
  ) {
    return await this.get<AllThreadsResponse>(
      formatUrl(apiUrls.insights.company.threads, params, query),
      allThreadsResponseSchema
    );
  }

  async streamThreadMessage(
    params: { companyId: string; threadId: string },
    callback: (chunk: string) => void
  ) {
    return this.streamResponse(
      formatUrl(apiUrls.insights.company.thread.messages, params),
      callback
    );
  }

  async createMessageForThread(
    params: { companyId: string; threadId: string },
    body: PromptBody,
    callback: (chunk: string) => void
  ) {
    return await this.streamResponse(
      formatUrl(apiUrls.insights.company.thread.messages, params),
      callback,
      {
        method: "POST",
        body: JSON.stringify(body),
      }
    );
  }

  async submitMessageFeedback(
    params: { companyId: string; threadId: string; messageId: string },
    body: FeedbackRequestBody
  ) {
    return await this.patch<FeedbackRequestBody, FeedbackResponse>({
      url: formatUrl(apiUrls.insights.company.thread.feedback, params),
      body,
      requestSchema: feedbackRequestBodySchema,
      responseSchema: feedbackResponseSchema,
    });
  }
}
